import { Navigate, useNavigate } from "react-router-dom";
import { useEffect } from "react";

/**
 * ProtectedRoute — guards a route by auth + optional role check.
 *
 * Props:
 *   children      — the page component to render
 *   requiredRoles — array of allowed roles, e.g. ["admin", "superadmin"]
 *                   if omitted, any authenticated user is allowed
 */

/** Default landing per role when they hit a page they cannot access */
const roleFallback = (role) =>
  role === "superadmin" ? "/dashboard" : "/invoiceGrid";

const ProtectedRoute = ({ children, requiredRoles, pageKey }) => {
  const navigate = useNavigate();
  const auth = JSON.parse(sessionStorage.getItem("auth") || "null");

  /* Periodically verify the session is still alive (handles manual clearance) */
  useEffect(() => {
    const check = () => {
      const current = JSON.parse(sessionStorage.getItem("auth") || "null");
      if (!current || !current.role) navigate("/", { replace: true });
    };
    const id = setInterval(check, 30_000); // every 30 s
    return () => clearInterval(id);
  }, [navigate]);

  /* No session → login */
  if (!auth || !auth.role) return <Navigate to="/" replace />;

  /* Wrong role → role's own home page (prevents infinite redirect) */
  if (requiredRoles && !requiredRoles.includes(auth.role)) {
    return <Navigate to={roleFallback(auth.role)} replace />;
  }

  /* Page-level access check — only applied to admin users with a pageKey set */
  if (auth.role === "admin" && pageKey) {
    const allowedPages = Array.isArray(auth.allowedPages) ? auth.allowedPages : [];
    if (allowedPages.length > 0 && !allowedPages.includes(pageKey)) {
      return <Navigate to={roleFallback(auth.role)} replace />;
    }
  }

  return children;
};

export default ProtectedRoute;
